import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { Summary, SummaryContent, SummaryItem } from '../../Summary/Summary';
import type { DetailedTicket } from '../types';

type PurchaseSummaryProps = ComponentProps<typeof Summary> & Pick<DetailedTicket, 'orderId' | 'submissionDate' | 'duration' | 'renewType'> & { numberOfDraws: number };

/**
 * Renders a summary of a lottery ticket purchase, including the order ID, purchase date, type, and duration.
 * Extends the Summary component and accepts all Summary props.
 *
 * @param {string} props.orderId - The identifier for the order.
 * @param {string} props.submissionDate - The date when the ticket was purchased.
 * @param {number} props.duration - The number of draws or duration of the ticket.
 * @param {number} props.numberOfDraws - The number of draws in the ticket.
 * @returns {JSX.Element} The rendered purchase summary component.
 */
function PurchaseSummary({ className, orderId, submissionDate, duration, renewType, numberOfDraws, ...props }: PurchaseSummaryProps) {
  const t = useTranslations('lotteries.ticket_details.purchase_summary');
  return (
    <Summary className={className} {...props}>
      <SummaryContent>
        <SummaryItem label={t('order_id')} value={orderId} />
        <SummaryItem label={t('date')} value={t('date_value', { date: new Date(submissionDate) })} />
        <SummaryItem label={t('type')} value="Standard" />
        {renewType === 'Weeks' && <SummaryItem label={t('duration')} value={t('duration_value', { numberOfDraws })} />}
        {renewType === 'Subscription' && <SummaryItem label={t('billing_period')} value={t('total_duration_subscription', { duration })} />}
        {renewType === 'Hunt' && <SummaryItem label={t('billing_period')} value={t('total_duration_hunt')} />}
      </SummaryContent>
    </Summary>
  );
}

export { PurchaseSummary };
